import React, { useEffect, useState, useCallback, useRef } from 'react';
import { fetchKioskData } from '../services/kioskApi';
import { Tile, DeviceTileCard } from './shared/DashboardTiles';

const STORAGE_KEY = 'kiosk_api_key';

interface KioskData {
    title: string;
    refresh_sec: number;
    fetched_at: string;
    tiles: Tile[];
}

// ── Clock ─────────────────────────────────────────────────────────────────────
const Clock: React.FC = () => {
    const [time, setTime] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {pad(time.getHours())}:{pad(time.getMinutes())}:{pad(time.getSeconds())}
        </span>
    );
};

// ── API key entry screen ───────────────────────────────────────────────────────
const ApiKeyPrompt: React.FC<{ onKey: (k: string) => void }> = ({ onKey }) => {
    const [val, setVal] = useState('');
    return (
        <div style={{
            minHeight: '100vh', background: '#0f172a', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                background: '#1e293b', borderRadius: 16, padding: '40px 48px',
                maxWidth: 440, width: '100%', textAlign: 'center',
            }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🖥️</div>
                <h2 style={{ color: '#e2e8f0', marginTop: 0 }}>Kiosk-dashboard</h2>
                <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 24 }}>
                    Ange API-nyckel för att visa sensordata. Nyckeln sparas i webbläsaren.
                </p>
                <input
                    type="password"
                    placeholder="API-nyckel"
                    value={val}
                    onChange={e => setVal(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && val && onKey(val)}
                    style={{
                        width: '100%', boxSizing: 'border-box',
                        padding: '12px 16px', borderRadius: 8,
                        border: '1px solid #334155', background: '#0f172a',
                        color: '#e2e8f0', fontSize: 16, marginBottom: 16,
                    }}
                />
                <button
                    disabled={!val}
                    onClick={() => onKey(val)}
                    style={{
                        width: '100%', padding: '12px', borderRadius: 8,
                        border: 'none', background: val ? '#3b82f6' : '#334155',
                        color: 'white', fontSize: 16, fontWeight: 700,
                        cursor: val ? 'pointer' : 'default',
                    }}
                >
                    Öppna dashboard
                </button>
            </div>
        </div>
    );
};

// ── Main Kiosk component ──────────────────────────────────────────────────────
const Kiosk: React.FC = () => {
    // Resolve API key: URL (helst i fragmentet, #apiKey=, som aldrig skickas till servern
    // eller hamnar i loggar) → localStorage. Nyckeln tas bort ur adressfältet direkt så att
    // den inte ligger kvar i historik eller bokmärken.
    const [apiKey, setApiKey] = useState<string | null>(() => {
        const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('apiKey');
        const fromQuery = new URLSearchParams(window.location.search).get('apiKey');
        const urlKey = fromHash || fromQuery;
        const k = urlKey || localStorage.getItem(STORAGE_KEY);
        if (k) localStorage.setItem(STORAGE_KEY, k);
        if (urlKey) {
            try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
        }
        return k;
    });

    const [data, setData] = useState<KioskData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchData = useCallback(async (key: string) => {
        try {
            setData(await fetchKioskData(key));
            setError(null);
        } catch (e: any) {
            if (e?.response?.status === 401) {
                setError('Ogiltig API-nyckel.');
                localStorage.removeItem(STORAGE_KEY);
                setApiKey(null);
            } else {
                setError(`Kunde inte hämta data: ${e?.response?.data?.message || e.message}`);
            }
        }
    }, []);

    // Initial fetch + auto-refresh
    useEffect(() => {
        if (!apiKey) return;
        fetchData(apiKey);
        const scheduleNext = (sec: number) => {
            timerRef.current = setTimeout(() => {
                fetchData(apiKey).then(() => {
                    scheduleNext(data?.refresh_sec ?? sec);
                });
            }, sec * 1000);
        };
        scheduleNext(data?.refresh_sec ?? 30);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiKey, fetchData]);

    const handleKey = (k: string) => {
        localStorage.setItem(STORAGE_KEY, k);
        setApiKey(k);
    };

    if (!apiKey) return <ApiKeyPrompt onKey={handleKey} />;

    const lastUpdated = data?.fetched_at
        ? new Date(data.fetched_at).toLocaleTimeString('sv-SE')
        : null;

    return (
        <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', fontFamily: 'system-ui, sans-serif' }}>

            {/* Header bar */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '18px 32px', background: '#1e293b',
                borderBottom: '1px solid #334155', position: 'sticky', top: 0, zIndex: 10,
            }}>
                <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: 0.3 }}>
                    {data?.title || '⟳ Laddar…'}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    {lastUpdated && (
                        <span style={{ fontSize: 13, color: '#64748b' }}>
                            Uppdaterad {lastUpdated}
                        </span>
                    )}
                    <span style={{ fontSize: 28, fontWeight: 700, color: '#93c5fd', fontVariantNumeric: 'tabular-nums' }}>
                        <Clock />
                    </span>
                    <button
                        onClick={() => { localStorage.removeItem(STORAGE_KEY); setApiKey(null); setData(null); }}
                        title="Byt API-nyckel"
                        style={{
                            background: 'rgba(255,255,255,.06)', border: '1px solid #334155',
                            color: '#94a3b8', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                        }}
                    >
                        🔑
                    </button>
                </div>
            </div>

            {/* Content */}
            <div style={{ padding: '28px 32px' }}>
                {error && (
                    <div style={{
                        background: 'rgba(248,113,113,.15)', border: '1px solid #7f1d1d',
                        borderRadius: 10, padding: '14px 18px', marginBottom: 20, color: '#fca5a5',
                    }}>
                        {error}
                    </div>
                )}

                {data && data.tiles.length === 0 && !error && (
                    <div style={{ textAlign: 'center', color: '#64748b', marginTop: 80 }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                        <p>Inga enheter konfigurerade. Gå till <strong>Kiosk-admin</strong> i adminpanelen för att lägga till sensorer.</p>
                    </div>
                )}

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                    gap: 20,
                }}>
                    {data?.tiles.map((tile, i) => (
                        <DeviceTileCard key={`tile-${tile.device_ieee}-${i}`} tile={tile} dark />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Kiosk;